import { CommentClient, NewComment } from 'api/clients/CommentClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { makeAutoObservable, observable, ObservableMap, values } from 'mobx'
import { Comment, CommentStatus } from 'types'
import { CommentableType } from 'components/ActivityEvent/Types'
import { defaultMobxCacheMaxAge, type RootStore } from './RootStore'
import { CollabItemType, CommentItem } from './DocStore.types'
import { reviewFilter } from './CollabItemsFilters'
import { makePersistable } from 'mobx-persist-store'

export class CommentsStore {
  rootStore: RootStore
  comments: ObservableMap<number, Comment> = observable.map()
  isLoading: boolean = false
  isUpdating: boolean = false
  highlighted?: Comment = undefined
  selected?: Comment = undefined

  constructor(rootStore: RootStore, shouldPersist = true) {
    makeAutoObservable(this)

    if (shouldPersist) {
      makePersistable(this, {
        name: 'CommentsStore',
        properties: ['comments'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }

    this.rootStore = rootStore
  }

  select = (id?: number) => {
    if (id === undefined) {
      this.selected = undefined
    } else {
      this.selected = this.get(id)
      this.markAsRead(id)
      if (this.highlighted?.id === id) {
        this.highlight(undefined)
      }
    }
  }

  highlight = (id?: number) => {
    if (this.selected && id === this.selected.id) return
    if (id === undefined) {
      this.highlighted = undefined
    } else {
      this.highlighted = this.get(id)
    }
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
  }

  setUpdating = (updating: boolean) => {
    this.isUpdating = updating
  }

  /**
   * Comment CRUD
   */

  add = (comment: Comment) => {
    const alreadyInSet = this.comments.has(comment.id)
    this.comments.set(comment.id, comment)
    comment.replies?.forEach((reply) => this.add(reply))

    if (alreadyInSet) return

    this.markCommentForCleanup(comment)
  }

  bulkAdd = (comments: Comment[]) => {
    comments.forEach((comment) => this.add(comment))
  }

  get = (id: number) => {
    return this.comments.get(id)
  }

  getByUuid = (uuid: string) => {
    return this.all.find((comment) => comment.uuid === uuid)
  }

  isUuidSelected = (uuid: string) => {
    if (!this.selected) return false
    return this.getByUuid(uuid)?.id === this.selected?.id
  }

  get all() {
    return values(this.comments)
  }

  getByCommentable = (id: number | string, type: CommentableType) => {
    return this.all.filter((comment) => comment.commentable_id === id && comment.commentable_type === type)
  }

  remove = (id: number) => {
    this.comments.delete(id)
  }

  updateAttrs = (id: number, attributes: Partial<Comment>) => {
    const comment = this.get(id)
    if (!comment) return
    Object.assign(comment, attributes)
  }

  /**
   * API operations
   */

  load = async (articleId: string) => {
    this.setLoading(true)
    const response = await CommentClient.getArticleComments(articleId)
    if (response.type === SUCCESS_RESPONSE_TYPE && response.data.comments) {
      this.bulkAdd(response.data.comments)
    }
    this.setLoading(false)
  }

  delete = async (id: number, options?: { trackArticleUpdate: boolean }) => {
    const response = await CommentClient.deleteComment(id, options?.trackArticleUpdate)

    if (this.rootStore.docStore?.inCleanupMode) {
      this.rootStore.docStore?.cleanupModeStore?.clean(CollabItemType.Comment)
    }

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      this.rootStore.docStore?.cleanupModeStore?.dirty(CollabItemType.Comment)
      throw new Error(response.data.message)
    }

    this.remove(id)
  }

  create = async (
    comment: NewComment,
    mentionedUsersIds: number[] = [],
    isRichText: boolean,
    anonymousUserDisplayName?: string,
    userId?: string,
  ) => {
    const response = await CommentClient.createComment(
      comment,
      mentionedUsersIds,
      anonymousUserDisplayName,
      userId,
      isRichText,
    )

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.add(response.data.comment)
    } else {
      throw new Error(response.data.message)
    }
  }

  update = async (
    id: number,
    payload: { comment?: string; title?: string },
    mentionedUsers: number[] = [],
    isRichText: boolean,
  ) => {
    if (this.isUpdating) return

    const comment = this.get(id)
    if (!comment) return

    this.setUpdating(true)

    /**
     * Optimistic update
     */
    const oldAttrs = { ...comment }
    this.updateAttrs(comment.id, payload)

    const response = await CommentClient.updateComment(id, payload, mentionedUsers, isRichText)
    this.setUpdating(false)

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(comment.id, response.data.comment)
    } else {
      this.updateAttrs(comment.id, oldAttrs)
      throw new Error(response.data.message)
    }
  }

  togglePin = async (id: number) => {
    const comment = this.get(id)
    if (!comment) return
    const { important } = comment
    this.updateAttrs(comment.id, { important: !important })

    const response = await CommentClient.toggleImportance(comment.id)

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(comment.id, { important })
      throw new Error(response.data.message)
    }
  }

  toggleResolved = async (id: number) => {
    const comment = this.get(id)
    if (!comment) return
    const { status } = comment
    this.updateAttrs(id, {
      status: status === CommentStatus.Resolved ? CommentStatus.Published : CommentStatus.Resolved,
    })

    if (this.rootStore.docStore?.inCleanupMode && status !== CommentStatus.Resolved) {
      this.rootStore.docStore?.cleanupModeStore?.clean(CollabItemType.Comment)
    }

    const response =
      status === CommentStatus.Resolved
        ? await CommentClient.unresolveComment(comment.id)
        : await CommentClient.resolveComment(comment.id)

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(id, { status })
      this.rootStore.docStore?.cleanupModeStore?.dirty(CollabItemType.Comment)
      throw new Error(response.data.message)
    }
  }

  bulkResolve = async (comments: Comment[]) => {
    if (comments.length < 1) return

    comments.forEach((comment) => this.updateAttrs(comment.id, { status: CommentStatus.Resolved }))
    const response = await CommentClient.bulkResolveComments(comments.map((comment) => comment.id))

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      comments.forEach((comment) => this.updateAttrs(comment.id, { status: CommentStatus.Published }))

      throw new Error(response.data)
    }
  }

  bulkDestroy = async (articleId: string, layerId: string, comments: Comment[]) => {
    comments.forEach(({ id }) => this.comments.delete(id))
    const response = await CommentClient.bulkDestroyComments(
      articleId,
      layerId,
      comments.map((comment) => comment.id),
    )

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      comments.forEach((comment) => this.add(comment))
      throw new Error(response.data)
    }
  }

  toggleReaction = async (id: number, reaction: string) => {
    const comment = this.get(id)
    if (!comment) return

    const response = await CommentClient.toggleCommentReaction(id, reaction)
    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(id, response.data.comment)
    } else {
      throw new Error(response.data.message)
    }
  }

  markAsRead = (id: number) => {
    const comment = this.get(id)
    if (!comment) return
    const replies = this.getByCommentable(id, CommentableType.COMMENT)
    ;[comment, ...replies].forEach(({ id, read_status }) => {
      if (!read_status) {
        CommentClient.markCommentAsRead(id)
        this.updateAttrs(id, { read_status: true })
      }
    })
  }

  private markCommentForCleanup(comment: Comment) {
    if (!this.rootStore.docStore) return

    if (!this.rootStore.docStore.inCleanupMode) return

    const currentReview = this.rootStore.docStore.cleanupModeStore.currentReview

    const isCommentRelevant =
      (currentReview &&
        reviewFilter.fn({ type: CollabItemType.Comment, item: comment } as CommentItem, currentReview)) ||
      !currentReview

    if (isCommentRelevant) {
      // update cleanup outstanding items count
      this.rootStore.docStore.cleanupModeStore.markForCleaning(CollabItemType.Comment)
    }
  }
}
